import { Fragment, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { cn } from '../lib/cn'
import { EmptyState, ErrorState, Skeleton } from './States'

/* ------------------------------------------------------------------
   جدول داده — سند مادر بند ۱۱.۱
   ------------------------------------------------------------------
   الزامات رعایت‌شده:
   · Sort/Filter فقط از Allowlist (بند ۱۵) — ستون باید sortKey داشته باشد
   · Unbounded List ممنوع (بند ۱۵.۲) — صفحه‌بندی بیرون از جدول اجباری است
   · حالت‌های Empty/Error/Loading استاندارد
   · در موبایل (زیر md) هر ردیف یک «کارت» می‌شود، نه جدولِ اسکرول‌شونده
   ------------------------------------------------------------------ */

/*
 | چرا کارت و نه اسکرول افقی؟
 |
 | نسخه اول جدول را در موبایل افقی اسکرول می‌کرد. نتیجه این بود که
 | کاربر موبایل ستون «وضعیت» یا «مهلت» را اصلاً نمی‌دید مگر اینکه
 | حدس بزند باید بکشد. در RTL هم جهت اسکرول گیج‌کننده بود.
 |
 | حالا زیر md هر ردیف یک کارت است: عنوان + نشان وضعیت در خط اول،
 | بقیه ستون‌ها جفتِ «برچسب/مقدار». هیچ صفحه‌ای لازم نیست تغییر کند —
 | نقش‌ها اگر تعیین نشوند از روی ترتیب ستون‌ها حدس زده می‌شود.
 */

export interface Column<T> {
  /** کلید یکتای ستون */
  key: string
  header: ReactNode
  /** رندر سلول */
  cell: (row: T, index: number) => ReactNode
  /** اگر ست شود ستون قابل مرتب‌سازی است — همان کلید به Backend می‌رود */
  sortKey?: string
  align?: 'start' | 'center' | 'end'
  width?: string
  /** در موبایل پنهان شود — در نمای کارتی هم رندر نمی‌شود */
  hideOnMobile?: boolean
  /** محتوای عددی — هم‌ترازی tabular */
  numeric?: boolean
  /**
   * جای این ستون در کارتِ موبایل:
   * · `title`   — خط اول کارت، درشت (پیش‌فرض: اولین ستون)
   * · `caption` — زیرِ عنوان، ریز و کم‌رنگ (پیش‌فرض: دومین ستون اگر عنوان جدا باشد)
   * · `badge`   — گوشه خط اول، کنار عنوان (پیش‌فرض: ستونی با کلید status/state)
   * · `field`   — جفت «برچسب/مقدار» در بدنه کارت (پیش‌فرض بقیه)
   * · `hidden`  — فقط در جدول دسکتاپ دیده شود
   */
  mobileRole?: MobileRole
}

export type MobileRole = 'title' | 'caption' | 'badge' | 'field' | 'hidden'

export type SortDirection = 'asc' | 'desc'

export interface SortState {
  key: string
  direction: SortDirection
}

export interface DataTableProps<T> {
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T, index: number) => string | number
  loading?: boolean
  error?: { message?: string; requestId?: string | null } | null
  onRetry?: () => void
  sort?: SortState | null
  onSortChange?: (sort: SortState) => void
  onRowClick?: (row: T) => void
  emptyTitle?: string
  emptyDescription?: ReactNode
  emptyAction?: ReactNode
  /**
   * نشانی تصویر سه‌بعدی حالت خالی — به EmptyState پاس می‌شود.
   * مثل خودِ EmptyState نشانی می‌گیرد و مسیر نمی‌سازد.
   */
  emptyIllustration?: string
  /** تعداد ردیف اسکلت هنگام بارگذاری */
  skeletonRows?: number
  caption?: string
  className?: string
  /**
   * نمای موبایل. پیش‌فرض `cards`.
   * `accordion` وقتی خوب است که ستون‌ها زیادند و فقط عنوان مهم است:
   * کارت جمع می‌شود و با ضربه باز می‌شود.
   * `table` رفتار قدیمی (اسکرول افقی) را برمی‌گرداند.
   */
  mobileVariant?: 'cards' | 'accordion' | 'table'
}

/* ------------------------------------------------------------------
   تعیین نقش ستون‌ها در نمای موبایل
   ------------------------------------------------------------------ */

/** کلیدهایی که تقریباً همیشه «نشان» هستند — برای حدسِ پیش‌فرض */
const BADGE_KEYS = /^(status|state|priority|tone|level|badge)$/i

function resolveMobileRoles<T>(columns: Column<T>[]) {
  const visible = columns.filter((c) => !c.hideOnMobile && c.mobileRole !== 'hidden')

  const explicit = (role: MobileRole) => visible.filter((c) => c.mobileRole === role)
  const unassigned = visible.filter((c) => !c.mobileRole)

  /*
   | عنوان: صریح ← اولین ستونِ بی‌نقش ← اولین ستون دیده‌شونده.
   |
   | ⚠️ حلقه سوم لازم است. اگر صفحه‌ای برای همه ستون‌ها نقش صریح بدهد
   | ولی هیچ‌کدام `title` نباشد، `unassigned` خالی می‌شود و کارت بدون
   | عنوان رندر می‌شد — یعنی فهرستی از کارت‌های بی‌نام.
   */
  const title = explicit('title')[0] ?? unassigned[0] ?? visible[0]

  /* نشان: یا صریح، یا اولین ستونی که کلیدش status/priority/… است. */
  const badge =
    explicit('badge')[0] ?? unassigned.find((c) => c !== title && BADGE_KEYS.test(c.key))

  const caption = explicit('caption')[0]

  /* بقیه ستون‌ها بدنه کارت‌اند — به‌جز آن‌هایی که بالا مصرف شدند. */
  const used = new Set([title, badge, caption].filter(Boolean))
  const fields = visible.filter((c) => !used.has(c))

  return { title, badge, caption, fields }
}

const ALIGN = {
  start: 'text-start',
  center: 'text-center',
  end: 'text-end',
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  error = null,
  onRetry,
  sort = null,
  onSortChange,
  onRowClick,
  emptyTitle = 'موردی یافت نشد',
  emptyDescription,
  emptyAction,
  emptyIllustration,
  skeletonRows = 6,
  caption,
  className,
  mobileVariant = 'cards',
}: DataTableProps<T>) {
  if (error) {
    return <ErrorState description={error.message} requestId={error.requestId} onRetry={onRetry} />
  }

  const showEmpty = !loading && (!rows || rows.length === 0)

  const handleSort = (column: Column<T>) => {
    if (!column.sortKey || !onSortChange) return
    const isActive = sort?.key === column.sortKey
    onSortChange({
      key: column.sortKey,
      direction: isActive && sort?.direction === 'asc' ? 'desc' : 'asc',
    })
  }

  const mobile = mobileVariant !== 'table'

  return (
    <div className={cn('w-full', className)}>
      {mobile && (
        <MobileList
          columns={columns}
          rows={rows}
          rowKey={rowKey}
          loading={loading}
          onRowClick={onRowClick}
          skeletonRows={skeletonRows}
          collapsible={mobileVariant === 'accordion'}
        />
      )}

      <div className={cn('w-full overflow-x-auto', mobile && 'hidden md:block')}>
      <table className="w-full border-collapse text-[length:var(--pf-text-sm)]">
        {caption && <caption className="pf-sr-only">{caption}</caption>}

        {/*
          ⚠️ سرصفحه زمینهٔ خاکستری می‌گیرد و می‌چسبد بالا.

          🔴 پیش‌تر سرصفحه فقط یک ردیفِ متنِ کم‌رنگ بود با همان زمینهٔ
          سفیدِ بدنه؛ در فهرستی با ۲۰ ردیف، هیچ چیز آن را از داده جدا
          نمی‌کرد. حالا `--pf-surface-muted` — همان خاکستریِ کنترل‌های
          Filled — مرزِ «برچسب» و «داده» را می‌سازد.

          ⚠️ `sticky` لازم است چون جدول‌های این سامانه تا ۵۰ ردیف
          می‌روند و با اسکرول، نامِ ستون‌ها از بالا بیرون می‌رفت و
          کاربر نمی‌دانست هر ستون چیست. `z-` پایین است تا زیر مدال و
          کمبوباکس بماند.
        */}
        {/*
          ⚠️ زمینهٔ سرصفحه `--pf-surface` است و نه `--pf-surface-muted`.

          🔴 نسخهٔ اول خاکستری بود، ولی ردیف‌های فردِ راه‌راه هم دقیقاً
          همان خاکستری‌اند؛ نتیجه این شد که سرصفحه با ردیف اول یکی
          دیده می‌شد و اصلاً شبیه سرصفحه نبود. حالا سفید می‌ماند و
          تفکیک از **خطِ ضخیمِ زیرش** و وزنِ متن می‌آید — یعنی
          سرصفحه روشن‌تر از داده است نه هم‌رنگِ یکی از حالت‌هایش.
        */}
        <thead className="sticky top-0 z-[var(--pf-z-sticky)] bg-[var(--pf-surface)]">
          <tr className="border-b-2 border-[var(--pf-border-strong)]">
            {columns.map((column) => {
              const sortable = !!column.sortKey && !!onSortChange

              /*
               | 🔴 هر سه شرط لازم است.
               |
               | نسخه اول فقط `sort?.key === column.sortKey` بود. وقتی
               | جدولی نه `sort` می‌گرفت و نه ستون `sortKey` داشت،
               | `undefined === undefined` می‌شد **true** و خط بعد روی
               | `sort!.direction` می‌شکست.
               |
               | یعنی هر جدول بدون مرتب‌سازی — که حالت کاملاً عادی است —
               | کل صفحه را می‌انداخت. در ماژول گزارش کار دیده نشد چون
               | جدول‌هایش همیشه sortKey داشتند.
               */
              const isActive = !!sort && !!column.sortKey && sort.key === column.sortKey

              return (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  aria-sort={
                    isActive && sort ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  className={cn(
                    /* ⚠️ `py-3` و نه `py-2.5`: سرصفحه حالا زمینه دارد و
                       با فاصلهٔ کم، متن به لبهٔ نوار می‌چسبید. */
                    'whitespace-nowrap px-3 py-3 font-[var(--pf-weight-semibold)]',
                    'text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]',
                    ALIGN[column.align ?? 'start'],
                    column.hideOnMobile && 'hidden md:table-cell',
                  )}
                >
                  {sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(column)}
                      /* ⚠️ `-mx-1.5 px-1.5` تا هالهٔ hover کمی از متن
                         بزرگ‌تر باشد بی‌آنکه ستون را جابه‌جا کند —
                         نشانهٔ اینکه سرستون کلیک‌شدنی است. */
                      className={cn(
                        'group -mx-1.5 inline-flex items-center gap-1 rounded-[var(--pf-radius-sm)] px-1.5 py-0.5',
                        'transition-colors duration-[var(--pf-duration-fast)]',
                        'hover:bg-[var(--pf-surface-muted)] hover:text-[var(--pf-text)]',
                        isActive && 'text-[var(--pf-text)]',
                      )}
                    >
                      {column.header}
                      <SortIcon active={isActive} direction={sort?.direction} />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>

        <tbody>
          {/* ⚠️ اسکلت هم راه‌راه است تا لحظهٔ جایگزینی با دادهٔ واقعی
              پرشِ بصری ندهد. */}
          {loading &&
            Array.from({ length: skeletonRows }).map((_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} className="odd:bg-[var(--pf-surface-muted)]">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn('px-3 py-3', column.hideOnMobile && 'hidden md:table-cell')}
                  >
                    <Skeleton className="h-3.5" />
                  </td>
                ))}
              </tr>
            ))}

          {/*
            🔴 راه‌راه به‌جای خطِ زیرِ هر ردیف.

            ⚠️ پیش‌تر هر ردیف `border-b` داشت. در فهرستی با ۲۰ ردیف
            یعنی ۲۰ خطِ افقیِ تمام‌عرض، و چشم به‌جای داده، خط‌ها را
            دنبال می‌کرد — جدول شبیه نردبان دیده می‌شد.

            راه‌راهِ ملایم همان کار را می‌کند (تفکیکِ ردیف و ردگیریِ
            افقی در جدولِ عریض) بدون آنکه خطی بکشد. `nth-child(odd)`
            گرفته می‌شود تا ردیف اول رنگی باشد و مرزش با سرصفحه پیدا.

            ⚠️ رنگِ hover باید از راه‌راه **پررنگ‌تر** باشد وگرنه روی
            ردیف‌های رنگی هیچ تغییری دیده نمی‌شود؛ `--pf-primary-soft`
            هم فام برند را می‌آورد و هم از هر دو حالت متمایز است.
          */}
          {!loading &&
            rows?.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'transition-colors duration-[var(--pf-duration-fast)]',
                  'odd:bg-[var(--pf-surface-muted)]',
                  onRowClick &&
                    'cursor-pointer hover:bg-[var(--pf-primary-soft)] odd:hover:bg-[var(--pf-primary-soft)]',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      /* ⚠️ `py-3` — ردیف با ارتفاع قبلی (~۴۰px) برای
                         قرصِ وضعیتِ ۲۴ پیکسلی داخلش تنگ بود و قرص
                         تقریباً کلِ ارتفاع را پر می‌کرد. */
                      'px-3 py-3 text-[var(--pf-text)] align-middle',
                      ALIGN[column.align ?? 'start'],
                      column.numeric && 'pf-tnum',
                      column.hideOnMobile && 'hidden md:table-cell',
                    )}
                  >
                    {column.cell(row, index)}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
      </div>

      {showEmpty && (
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
          illustration={emptyIllustration}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------
   نمای موبایل — هر ردیف یک کارت
   ------------------------------------------------------------------ */

interface MobileListProps<T> {
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T, index: number) => string | number
  loading: boolean
  onRowClick?: (row: T) => void
  skeletonRows: number
  collapsible: boolean
}

function MobileList<T>({
  columns,
  rows,
  rowKey,
  loading,
  onRowClick,
  skeletonRows,
  collapsible,
}: MobileListProps<T>) {
  const { title, badge, caption, fields } = resolveMobileRoles(columns)

  if (loading) {
    return (
      <ul className="flex flex-col gap-[var(--pf-space-3)] p-[var(--pf-space-3)] md:hidden">
        {Array.from({ length: Math.min(skeletonRows, 4) }).map((_, index) => (
          <li
            key={`skeleton-card-${index}`}
            className="rounded-[var(--pf-radius-md)] border border-[var(--pf-border)] p-[var(--pf-space-3)]"
          >
            <Skeleton className="h-4 w-1/2" />
            <div className="mt-3">
              <Skeleton lines={2} />
            </div>
          </li>
        ))}
      </ul>
    )
  }

  if (!rows || rows.length === 0) return null

  return (
    <ul className="flex flex-col gap-[var(--pf-space-3)] p-[var(--pf-space-3)] md:hidden">
      {rows.map((row, index) => (
        <MobileCard
          key={rowKey(row, index)}
          row={row}
          index={index}
          title={title}
          badge={badge}
          caption={caption}
          fields={fields}
          onRowClick={onRowClick}
          collapsible={collapsible}
        />
      ))}
    </ul>
  )
}

interface MobileCardProps<T> {
  row: T
  index: number
  title?: Column<T>
  badge?: Column<T>
  caption?: Column<T>
  fields: Column<T>[]
  onRowClick?: (row: T) => void
  collapsible: boolean
}

function MobileCard<T>({
  row,
  index,
  title,
  badge,
  caption,
  fields,
  onRowClick,
  collapsible,
}: MobileCardProps<T>) {
  const [open, setOpen] = useState(false)

  const head = (
    <>
      <div className="min-w-0 flex-1">
        {title && (
          /*
           | ⚠️ `truncate` نه. عنوانِ کارت تنها چیزی است که کاربر برای
           | تشخیص ردیف دارد؛ بریدنش با «…» یعنی دو تیکت با شروع یکسان
           | در موبایل غیرقابل‌تفکیک شوند. تا دو خط اجازه شکستن دارد.
           */
          <div className="line-clamp-2 text-[length:var(--pf-text-sm)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]">
            {title.cell(row, index)}
          </div>
        )}
        {caption && (
          <div className="mt-0.5 truncate text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
            {caption.cell(row, index)}
          </div>
        )}
      </div>
      {badge && <div className="shrink-0">{badge.cell(row, index)}</div>}
    </>
  )

  const body = fields.length > 0 && (
    <dl className="mt-[var(--pf-space-3)] grid grid-cols-[auto_1fr] gap-x-[var(--pf-space-3)] gap-y-1.5 border-t border-[var(--pf-border)] pt-[var(--pf-space-3)]">
      {fields.map((column) => (
        <Fragment key={column.key}>
          <dt className="text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
            {column.header}
          </dt>
          <dd
            className={cn(
              'min-w-0 text-[length:var(--pf-text-sm)] text-[var(--pf-text)] text-end',
              column.numeric && 'pf-tnum',
            )}
          >
            {column.cell(row, index)}
          </dd>
        </Fragment>
      ))}
    </dl>
  )

  /*
   | آکاردئون: ضربه روی سر کارت باز/بسته می‌کند.
   | ⚠️ وقتی onRowClick هم هست، اولویت با باز کردن است — نه رفتن به صفحه.
   | دو کار متفاوت با یک ضربه، روی موبایل یعنی کاربر تصادفی صفحه عوض می‌کند.
   | برای همین در حالت آکاردئون رفتن به صفحه یک دکمه جدا در انتهای کارت است.
   */
  if (collapsible && body) {
    return (
      <li className="rounded-[var(--pf-radius-md)] border border-[var(--pf-border)] bg-[var(--pf-surface)]">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex w-full items-center gap-[var(--pf-space-3)] p-[var(--pf-space-3)] text-start"
        >
          {head}
          <Chevron open={open} />
        </button>

        {open && (
          <div className="px-[var(--pf-space-3)] pb-[var(--pf-space-3)]">
            {body}
            {onRowClick && (
              <button
                type="button"
                onClick={() => onRowClick(row)}
                className="mt-[var(--pf-space-3)] w-full rounded-[var(--pf-radius-sm)] bg-[var(--pf-surface-muted)] py-2 text-[length:var(--pf-text-sm)] text-[var(--pf-text)]"
              >
                مشاهده جزئیات
              </button>
            )}
          </div>
        )}
      </li>
    )
  }

  /*
   | ==================================================================
   | 🔴 چرا کارت **دکمه نیست**، حتی وقتی کلیک‌پذیر است:
   |
   | ستون‌های «اقدام» خودشان `<button>` دارند (مثلاً «انصراف» در
   | «مرخصی من» یا «تأیید»/«رد» در کارتابل‌ها). اگر کلِ کارت هم یک
   | `<button>` باشد، دکمه داخلِ دکمه می‌نشیند.
   |
   | ⚠️ این HTML **نامعتبر** است: React برای هر ردیف هشدار
   | «<button> cannot be a descendant of <button>» می‌داد — در صفحهٔ
   | «مرخصی من» ۵۰ خطای کنسول در یک بار باز شدن. تستِ دودِ همان صفحه
   | هم دقیقاً روی همین افتاد.
   |
   | ⚠️ و رفتارش هم شکننده بود: کلیکِ دکمهٔ داخلی فقط به این دلیل کار
   | می‌کرد که مصرف‌کننده یادش بود `stopPropagation` بنویسد.
   |
   | 🔴 حالا `div` با `role="button"` است: DOM معتبر می‌شود، رفتارِ
   | «ضربه روی کارت = باز کردن» دست‌نخورده می‌ماند، و صفحه‌کلید هم
   | مثل قبل کار می‌کند (Enter و Space).
   | ==================================================================
   */
  return (
    <li>
      <div
        {...(onRowClick
          ? {
              role: 'button' as const,
              tabIndex: 0,
              onClick: () => onRowClick(row),
              onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                if (event.key !== 'Enter' && event.key !== ' ') return

                /*
                 | ⚠️ اگر ضربه روی دکمهٔ داخلیِ کارت بوده، کارت نباید
                 | هم‌زمان باز شود.
                 */
                if (event.target !== event.currentTarget) return

                event.preventDefault()
                onRowClick(row)
              },
            }
          : {})}
        className={cn(
          'block w-full rounded-[var(--pf-radius-md)] border border-[var(--pf-border)]',
          'bg-[var(--pf-surface)] p-[var(--pf-space-3)] text-start',
          onRowClick && 'transition-colors duration-[var(--pf-duration-fast)] active:bg-[var(--pf-surface-hover)]',
        )}
      >
        <div className="flex items-start gap-[var(--pf-space-3)]">{head}</div>
        {body}
      </div>
    </li>
  )
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      className={cn(
        'size-3 shrink-0 text-[var(--pf-text-muted)] transition-transform duration-[var(--pf-duration-fast)]',
        open && 'rotate-180',
      )}
    >
      <path d="M2.5 4.5 6 8l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: SortDirection }) {
  return (
    <svg viewBox="0 0 12 12" fill="none" className="size-3 shrink-0" aria-hidden="true">
      <path
        d="M6 2.5 3.5 5.5h5z"
        fill="currentColor"
        opacity={active && direction === 'asc' ? 1 : 0.3}
      />
      <path
        d="M6 9.5 3.5 6.5h5z"
        fill="currentColor"
        opacity={active && direction === 'desc' ? 1 : 0.3}
      />
    </svg>
  )
}
